import React from 'react';
import { FaOilCan, FaCar, FaTools, FaWrench, FaCogs, FaBatteryFull, FaTachometerAlt, FaShieldAlt } from 'react-icons/fa';
import { useInView } from '../hooks/useInView';

const Services = () => {
  const sectionRef = useInView();
  const services = [
    {
      icon: <FaOilCan className="text-4xl" />,
      title: 'Troca de Óleo',
      description: 'Troca completa de óleo e filtros com produtos de qualidade premium.',
      features: ['Óleo sintético ou mineral', 'Filtros originais', 'Verificação de níveis']
    },
    {
      icon: <FaCar className="text-4xl" />,
      title: 'Sistema de Freios',
      description: 'Manutenção e reparo completo do sistema de freios do seu veículo.',
      features: ['Pastilhas e discos', 'Fluido de freio', 'Freio de mão']
    },
    {
      icon: <FaTools className="text-4xl" />,
      title: 'Suspensão',
      description: 'Revisão e troca de componentes da suspensão para maior conforto.',
      features: ['Amortecedores', 'Molas e buchas', 'Alinhamento']
    },
    {
      icon: <FaWrench className="text-4xl" />,
      title: 'Manutenção Preventiva',
      description: 'Check-up completo para evitar problemas futuros no seu carro.',
      features: ['Inspeção de 50 pontos', 'Relatório detalhado', 'Recomendações']
    },
    {
      icon: <FaCogs className="text-4xl" />,
      title: 'Motor',
      description: 'Diagnóstico e reparo de problemas no motor do seu veículo.',
      features: ['Diagnóstico computadorizado', 'Retífica', 'Troca de peças']
    },
    {
      icon: <FaBatteryFull className="text-4xl" />,
      title: 'Sistema Elétrico',
      description: 'Manutenção completa do sistema elétrico e eletrônico.',
      features: ['Bateria', 'Alternador', 'Sistema de ignição']
    },
    {
      icon: <FaTachometerAlt className="text-4xl" />,
      title: 'Injeção Eletrônica',
      description: 'Limpeza e regulagem do sistema de injeção eletrônica.',
      features: ['Limpeza de bicos', 'Regulagem', 'Scanner automotivo']
    },
    {
      icon: <FaShieldAlt className="text-4xl" />,
      title: 'Ar Condicionado',
      description: 'Manutenção e recarga do sistema de ar condicionado.',
      features: ['Recarga de gás', 'Higienização', 'Troca de filtros']
    }
  ];

  return (
    <section id="servicos" className="py-20 bg-gray-50" ref={sectionRef}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="animate-on-scroll text-center max-w-3xl mx-auto mb-16">
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">Nossos Serviços</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-dark mt-3 mb-4">
            Soluções Completas para Seu Veículo
          </h2>
          <p className="text-gray-600 text-lg">
            Oferecemos uma ampla gama de serviços automotivos com equipamentos modernos 
            e profissionais qualificados para cuidar do seu carro.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className={`animate-on-scroll delay-${Math.min((index % 4 + 1) * 100, 400)} group bg-white rounded-xl p-6 shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 hover:border-accent/20`}
            >
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-accent/10 to-accent/5 rounded-lg flex items-center justify-center text-accent mb-4 group-hover:scale-110 transition-transform duration-300">
                {service.icon}
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-secondary-dark mb-3 group-hover:text-accent transition-colors">
                {service.title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                {service.description}
              </p>

              {/* Features */}
              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-500">
                    <svg className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Hover Effect Border */}
              <div className="mt-6 pt-4 border-t border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity">
                <a href="#contato" className="text-accent font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all">
                  Solicitar Orçamento
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="animate-on-scroll mt-16 bg-gradient-to-br from-secondary-dark via-black to-secondary rounded-2xl p-8 md:p-12 text-center text-white border border-primary/20 shadow-2xl">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Não encontrou o serviço que precisa?
          </h3>
          <p className="text-gray-200 mb-6 max-w-2xl mx-auto">
            Entre em contato conosco! Realizamos diversos outros serviços automotivos 
            e teremos prazer em ajudá-lo.
          </p>
          <a
            href="#contato"
            className="inline-block bg-accent hover:bg-accent-dark text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
          >
            Fale Conosco
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
